// ════════════════════════════════════════════════════════════════════════
//  Übungscodes: erzeugen, normalisieren, prüfen
//
//  Format: <tier>-<eigenschaft>-<vier Ziffern>, z. B. "esel-lila-4711".
//
//  Der Code ist eine reproduzierbare Zugangsmarke, kein Passwort. Er schützt
//  pseudonyme Übungsdaten – mehr Reibung würde nur die Kinder ausbremsen.
// ════════════════════════════════════════════════════════════════════════

import { TRAITS, ANIMALS } from "./wordlists.js";

/** Wort-Wort-Zahl, jeweils klein und ohne Umlaute. */
const CODE_PATTERN = /^[a-z]{2,20}-[a-z]{2,20}-\d{4}$/;

/**
 * Bringt eine Kindereingabe in die kanonische Form.
 *
 * Kinder tippen Leerzeichen statt Bindestriche, halten Umschalt gedrückt oder
 * erwischen den Bindestrich doppelt. Das alles zu verzeihen ist Bequemlichkeit
 * ohne Sicherheitspreis.
 *
 * Wichtig: das SQL macht nur `lower(trim(...))`. Diese Funktion darf also
 * gröber normalisieren, muss aber immer eine Form liefern, die `lower(trim(...))`
 * unverändert lässt – sonst laufen Client und Datenbank auseinander.
 */
export function normalizeCode(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Prüft nur die FORM, nicht die Zugehörigkeit zu den Wortlisten.
 *
 * Bewusst so: Wortlisten dürfen sich ändern, ohne dass alte, gültige Codes
 * plötzlich als Tippfehler abgewiesen werden. Ob es den Code gibt, entscheidet
 * die Datenbank – hier geht es nur darum, dem Kind einen offensichtlichen
 * Tippfehler ohne Server-Runde zu zeigen.
 */
export function hasCodeShape(code: string): boolean {
  return CODE_PATTERN.test(code);
}

export type Random = () => number;

const pick = <T>(list: readonly T[], random: Random): T => {
  const index = Math.floor(random() * list.length);
  // Math.random() liefert [0,1), aber ein eigener Zufall könnte 1 liefern.
  return list[Math.min(index, list.length - 1)]!;
};

/** Erzeugt einen einzelnen Code. `zufall` ist injizierbar, damit Tests deterministisch sind. */
export function makeCode(random: Random = Math.random): string {
  const tier = pick(ANIMALS, random);
  const trait = pick(TRAITS, random);
  const number = String(Math.floor(random() * 10000)).padStart(4, "0");
  return `${tier}-${trait}-${number}`;
}

/**
 * Erzeugt `anzahl` verschiedene Codes – der Normalfall im Klassenzimmer
 * ("25 Codes erzeugen").
 *
 * `vergeben` sind bereits benutzte Codes, die nicht erneut herauskommen dürfen.
 * Die Datenbank hat zusätzlich ein unique auf `students.code`; diese Prüfung
 * hier verhindert nur, dass ein Klassensatz mit einem Fehler abbricht.
 */
export function makeCodes(
  count: number,
  optionen: { random?: Random; taken?: Iterable<string> } = {},
): string[] {
  if (!Number.isInteger(count) || count < 0) {
    throw new RangeError("anzahl muss eine nicht-negative ganze Zahl sein");
  }
  const random = optionen.random ?? Math.random;
  const used = new Set(optionen.taken ?? []);
  const codes: string[] = [];

  // Obergrenze, damit ein kaputter Zufall nicht in eine Endlosschleife läuft.
  const maxAttempts = count * 50 + 100;
  for (let attempt = 0; codes.length < count && attempt < maxAttempts; attempt++) {
    const code = makeCode(random);
    if (used.has(code)) continue;
    used.add(code);
    codes.push(code);
  }
  if (codes.length < count) {
    throw new Error(
      `Es konnten nur ${codes.length} von ${count} verschiedenen Codes erzeugt werden`,
    );
  }
  return codes;
}

/** Anzahl möglicher Codes – für die Einordnung im Konzept. */
export const CODE_SPACE = ANIMALS.length * TRAITS.length * 10000;
